import { Link, useParams, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { SERVICES, SERVICE_GROUPS } from '@shared';
import { ServiceCard } from '../components/ServiceCard';
import { useSeo, useJsonLd, CANONICAL_ORIGIN } from '../lib/seo';
import { useAuth } from '../lib/auth-context';

export function ServiceDetail() {
  const { id } = useParams<{ id: string }>();
  const svc = SERVICES.find((s) => s.id === id);
  const { user } = useAuth();
  const isSignedInClient = user?.kind === 'client';

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  // Hook always called (Rules of Hooks); empty title is benign before redirect.
  useSeo({
    title: svc ? `${svc.name} — Omega Trade and Finance` : 'Service — Omega Trade',
    description: svc?.summary,
    canonicalPath: svc ? `/services/${svc.id}` : '/services',
  });

  // Per-page structured data: a Service node (no price — keeps us clear of the
  // 2% fee-floor representation, REQ-010) plus a breadcrumb trail. Mirrors copy
  // already published on the page; introduces no new regulatory claims.
  useJsonLd(
    svc
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: svc.name,
            serviceType: svc.long,
            description: svc.summary,
            category: SERVICE_GROUPS.find((g) => g.id === svc.group)?.label,
            areaServed: ['GB', 'AE', 'IN', 'EU'],
            url: `${CANONICAL_ORIGIN}/services/${svc.id}`,
            provider: {
              '@type': 'Organization',
              name: 'Omega Trade and Finance Ltd',
              url: CANONICAL_ORIGIN,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Services', item: `${CANONICAL_ORIGIN}/services` },
              { '@type': 'ListItem', position: 2, name: svc.name, item: `${CANONICAL_ORIGIN}/services/${svc.id}` },
            ],
          },
        ]
      : null,
  );

  if (!svc) return <Navigate to="/services" replace />;

  const group = SERVICE_GROUPS.find((g) => g.id === svc.group)!;
  const related = SERVICES.filter((s) => s.group === svc.group && s.id !== svc.id).slice(0, 3);

  return (
    <main>
      <section className="section" style={{ paddingBottom: 0 }}>
        <div className="container">
          <div style={{ fontSize: 13, color: 'var(--fg-mute)', marginBottom: 24 }}>
            <Link to="/services" style={{ borderBottom: '1px solid var(--rule)' }}>Services</Link>
            {' / '}{group.label}{' / '}<span style={{ color: 'var(--fg)' }}>{svc.name}</span>
          </div>

          <div className="svc-detail">
            <div>
              <span className="chip accent">{group.label}</span>
              <h1 style={{ marginTop: 16 }}>{svc.name}</h1>
              <p className="summary">{svc.summary}</p>

              <h3>What we deliver</h3>
              <ul className="bullets">
                {svc.detail.map((d, i) => <li key={i}>{d}</li>)}
              </ul>

              <div className="regnote">
                <strong>Regulatory framing.</strong> {svc.not_regulated}
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 32, flexWrap: 'wrap' }}>
                {isSignedInClient ? (
                  <Link className="btn accent" to={`/portal/requests/new?service=${svc.id}`}>
                    Submit brief in portal →
                  </Link>
                ) : (
                  <Link className="btn accent" to={`/contact?service=${svc.id}`}>
                    Enquire about this service
                  </Link>
                )}
                <Link className="btn ghost" to="/regulatory">Regulatory position</Link>
              </div>
              {!isSignedInClient && (
                <p style={{ fontSize: 12, color: 'var(--fg-mute)', marginTop: 12, lineHeight: 1.6 }}>
                  Already a portal client? <Link to="/portal/sign-in" style={{ color: 'var(--accent)' }}>Sign in →</Link> to submit a brief directly into your engagement workspace.
                </p>
              )}
            </div>

            <aside className="svc-aside">
              <h4>Engagement</h4>
              <div className="price">{svc.fee}</div>
              <div className="row"><span>Indicative lead time</span><span>{svc.lead}</span></div>
              <div className="row"><span>Client type</span><span>Body corporate only</span></div>
              <div className="row"><span>Engagement form</span><span>Written letter</span></div>
              <div className="row"><span>Geographies</span><span>UK · EU · GCC · IN · SEA</span></div>
              <div className="row"><span>Payment</span><span>Card · Bank transfer</span></div>
              <div className="row"><span>Cards accepted</span><span style={{ fontSize: 11 }}>AmEx · Visa · MC · UnionPay</span></div>
              <div className="row"><span>Currencies</span><span style={{ fontSize: 11 }}>GBP · USD · EUR · AED · INR · SGD</span></div>
              <div className="row"><span>Reference</span><span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{svc.long}</span></div>
            </aside>
          </div>

          {related.length > 0 && (
            <>
              <h3 style={{ marginTop: 80, marginBottom: 24, fontSize: 22 }}>Related services in {group.label}</h3>
              <div className="svc-grid">
                {related.map((r) => <ServiceCard key={r.id} svc={r} />)}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
